import { useState, useEffect } from 'react';
import { Bell, BellOff, Check, AlertCircle } from 'lucide-react';
import DismissButton from '@/components/ui/DismissButton';
import {
  ACCENT, ACCENT_BRIGHT, FONT_DISPLAY, FONT_BODY, FONT_MONO,
  TEXT, HAIRLINE, glow, microLabel, primaryButton, ghostButton,
} from '@/styles/premium';

interface NotificationPermissionPromptProps {
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
  autoShow?: boolean;
}

export function NotificationPermissionPrompt({
  onPermissionGranted,
  onPermissionDenied,
  autoShow = false
}: NotificationPermissionPromptProps) {
  const [show, setShow] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      const currentPermission = Notification.permission;
      setPermission(currentPermission);

      // Check if running in standalone mode (PWA installed)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          (window.navigator as any).standalone === true;

      // Check if user previously dismissed the prompt
      const dismissed = localStorage.getItem('notificationPromptDismissed');
      
      // Show prompt if:
      // 1. autoShow is enabled
      // 2. Permission is default (not granted or denied)
      // 3. Either not dismissed OR running in standalone mode (show again after install)
      if (autoShow && currentPermission === 'default' && (!dismissed || isStandalone)) {
        // Clear dismissal flag if in standalone mode to show prompt after PWA install
        if (isStandalone && dismissed) {
          localStorage.removeItem('notificationPromptDismissed');
        }
        setTimeout(() => setShow(true), 3000); // Show after 3 seconds
      }
    };

    checkPermission();
  }, [autoShow]);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    
    try {
      const newPermission = await Notification.requestPermission();
      setPermission(newPermission);

      if (newPermission === 'granted') {
        onPermissionGranted?.();
        setShow(false);
        console.log('✅ Notifications enabled successfully');
      } else {
        onPermissionDenied?.();
        console.log('❌ Notification permission denied');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('notificationPromptDismissed', 'true');
  };

  if (!show || permission === 'granted') {
    return null;
  }

  return (
    <div
      className="fixed left-3 right-3 sm:left-4 sm:right-4 md:left-auto md:right-4 md:w-96"
      style={{ zIndex: 50, bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg,rgba(16,16,16,.97) 0%,rgba(10,10,10,.97) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(20,184,166,.18)',
          borderRadius: 16,
          padding: 'clamp(16px,4vw,20px)',
          fontFamily: FONT_BODY,
          color: TEXT.primary,
          boxShadow: '0 18px 50px rgba(0,0,0,.55)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ ...glow(180, 0.14), top: -70, right: -50 }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
            <div
              style={{
                width: 38, height: 38, borderRadius: 11, flexShrink: 0,
                background: 'rgba(20,184,166,.15)',
                border: '1px solid rgba(20,184,166,.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Bell size={17} color={ACCENT} aria-hidden />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...microLabel, marginBottom: 4 }}>Reminders</div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: 400, letterSpacing: '-.01em', lineHeight: 1.1 }}>
                Never miss a class
              </div>
            </div>
            <DismissButton onClick={handleDismiss} />
          </div>

          <p style={{ fontSize: 12.5, lineHeight: 1.65, color: TEXT.muted, fontWeight: 300, marginBottom: 14 }}>
            Get a nudge before each class starts.
          </p>

          {/* Benefits */}
          <div
            style={{
              background: 'rgba(255,255,255,.03)',
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 12,
              padding: 14,
              marginBottom: 16,
              display: 'flex', flexDirection: 'column', gap: 9,
            }}
          >
            {['Timely class reminders', 'Schedule change alerts', 'Local notifications'].map(label => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Check size={12} color={ACCENT} aria-hidden style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: TEXT.secondary, fontWeight: 300 }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, borderTop: `1px solid ${HAIRLINE}`, paddingTop: 16 }}>
            <EnableBtn onClick={handleRequestPermission} isRequesting={isRequesting} />
            <LaterBtn onClick={handleDismiss} />
          </div>

          <p
            style={{
              fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.1em',
              color: TEXT.ghost, marginTop: 12, textAlign: 'center', textTransform: 'uppercase',
            }}
          >
            Change anytime in settings
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EnableBtn({ onClick, isRequesting }: { onClick: () => void; isRequesting: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={isRequesting}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...primaryButton,
        flex: 1,
        padding: '11px 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        opacity: isRequesting ? 0.6 : 1,
        cursor: isRequesting ? 'default' : 'pointer',
        background: hov && !isRequesting ? ACCENT_BRIGHT : ACCENT,
        borderColor: hov && !isRequesting ? ACCENT_BRIGHT : ACCENT,
      }}
    >
      {isRequesting ? (
        <>
          <span
            style={{
              width: 13, height: 13, borderRadius: '50%',
              border: '2px solid rgba(4,33,30,.35)', borderTopColor: '#04211E',
              animation: 'npspin .7s linear infinite',
            }}
          />
          Enabling
          <style>{'@keyframes npspin{to{transform:rotate(360deg)}}'}</style>
        </>
      ) : (
        <>
          <Bell size={14} aria-hidden /> Enable
        </>
      )}
    </button>
  );
}

function LaterBtn({ onClick }: { onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...ghostButton,
        padding: '11px 16px',
        background: hov ? 'rgba(255,255,255,.05)' : 'transparent',
        borderColor: hov ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.12)',
        color: hov ? 'rgba(255,255,255,.8)' : 'rgba(255,255,255,.6)',
      }}
    >
      Later
    </button>
  );
}

// Status indicator component
export function NotificationStatus() {
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    setPermission(Notification.permission);
  }, []);

  const getStatusInfo = () => {
    if (permission === 'granted') {
      return {
        icon: <Bell className="w-4 h-4 text-green-600" />,
        text: 'Notifications enabled',
        color: 'text-green-600'
      };
    } else if (permission === 'denied') {
      return {
        icon: <BellOff className="w-4 h-4 text-red-600" />,
        text: 'Notifications blocked',
        color: 'text-red-600'
      };
    } else {
      return {
        icon: <AlertCircle className="w-4 h-4 text-amber-600" />,
        text: 'Notifications disabled',
        color: 'text-amber-600'
      };
    }
  };

  const status = getStatusInfo();

  return (
    <div className={`flex items-center gap-2 text-sm ${status.color}`}>
      {status.icon}
      <span>{status.text}</span>
    </div>
  );
}
